import { 
  Cpu, 
  Shield, 
  Zap, 
  BarChart3, 
  Users, 
  Headphones,
  CheckCircle2,
  ArrowRight
} from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

interface AdvantageItem {
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  highlights: string[];
  color: string;
}

interface AdvantagesContent {
  title: string;
  subtitle: string;
  description: string;
  advantages: AdvantageItem[];
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Cpu,
  Shield,
  Zap,
  BarChart3,
  Users,
  Headphones,
};

const defaultContent: AdvantagesContent = {
  title: '为什么选择我们',
  subtitle: '核心优势',
  description: '多年行业深耕，打造专业、智能、可靠的船运发货管理系统',
  advantages: [
    {
      icon: 'Cpu',
      title: '智能化管理',
      subtitle: 'AI-Powered',
      description: '运用人工智能和大数据技术，实现智能调度、智能预警、智能决策，让管理更高效。',
      highlights: ['智能调度算法', 'AI异常检测', '预测性分析', '自动化流程'],
      color: 'from-blue-400 to-blue-600',
    },
    {
      icon: 'Shield',
      title: '安全可靠',
      subtitle: 'Secure & Reliable',
      description: '采用银行级数据加密技术，多重备份机制，确保数据安全不丢失，系统稳定运行。',
      highlights: ['数据加密传输', '多重备份机制', '权限分级管理', '操作日志追踪'],
      color: 'from-emerald-400 to-emerald-600',
    },
    {
      icon: 'Zap',
      title: '高效便捷',
      subtitle: 'Efficient & Convenient',
      description: '一码通行，全程无纸化，大幅提升作业效率，减少人工干预，降低运营成本。',
      highlights: ['扫码快速通行', '无纸化办公', '移动化操作', '实时同步'],
      color: 'from-amber-400 to-amber-600',
    },
    {
      icon: 'BarChart3',
      title: '数据可视',
      subtitle: 'Data Visualization',
      description: '直观的数据大屏和报表，多维度数据分析，助力管理者快速掌握业务动态。',
      highlights: ['实时数据大屏', '多维度报表', '自定义分析', '数据导出'],
      color: 'from-violet-400 to-violet-600',
    },
    {
      icon: 'Users',
      title: '协同作业',
      subtitle: 'Collaborative Work',
      description: '打通上下游环节，实现供应商、运输方、客户等多方协同，信息实时共享。',
      highlights: ['多方协同', '信息实时共享', '消息即时推送', '在线沟通'],
      color: 'from-pink-400 to-pink-600',
    },
    {
      icon: 'Headphones',
      title: '专业服务',
      subtitle: 'Professional Service',
      description: '7x24小时技术支持，专业实施团队，提供从咨询到上线的全程服务保障。',
      highlights: ['7x24小时支持', '专业实施团队', '定期培训', '持续升级'],
      color: 'from-cyan-400 to-cyan-600',
    },
  ],
};

export default function Advantages() {
  const { content } = useSiteContent('advantages');
  const data: AdvantagesContent = content?.content || defaultContent;

  const getIcon = (iconName: string) => {
    return iconMap[iconName] || Cpu;
  };

  return (
    <section id="advantages" className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-ocean-50 rounded-full mb-4">
            <span className="w-2 h-2 bg-ocean-500 rounded-full" />
            <span className="text-ocean-600 text-sm font-medium">{data.subtitle}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            {data.title}
          </h2>
          <p className="text-lg text-slate-600">
            {data.description}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {(data.advantages || defaultContent.advantages).map((advantage: AdvantageItem) => {
            const IconComponent = getIcon(advantage.icon);
            return (
              <div
                key={advantage.title}
                className="group relative bg-white rounded-2xl p-6 border border-slate-100 hover:border-ocean-200 hover:shadow-xl hover:shadow-ocean-500/10 transition-all duration-300 overflow-hidden"
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${advantage.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
                
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${advantage.color} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  <IconComponent className="w-7 h-7 text-white" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-1">{advantage.title}</h3>
                <p className="text-sm text-slate-400 mb-4">{advantage.subtitle}</p>

                <p className="text-slate-600 mb-5 leading-relaxed">
                  {advantage.description}
                </p>

                <div className="space-y-2">
                  {advantage.highlights.map((highlight: string) => (
                    <div key={highlight} className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-ocean-500 flex-shrink-0" />
                      <span className="text-sm text-slate-600">{highlight}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 bg-slate-50 rounded-2xl p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                先进的技术架构
              </h3>
              <p className="text-slate-600 mb-8 leading-relaxed">
                采用微服务架构，前后端分离设计，支持高并发、高可用，系统可弹性扩展，满足不同规模企业的需求。
              </p>
              
              <div className="space-y-4">
                {[
                  { title: '云原生架构', desc: '支持公有云、私有云、混合云部署' },
                  { title: '微服务设计', desc: '模块化设计，灵活扩展' },
                  { title: '容器化部署', desc: 'Docker容器化，快速部署上线' },
                  { title: 'DevOps支持', desc: '持续集成，持续交付' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-ocean-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle2 className="w-4 h-4 text-ocean-600" />
                    </div>
                    <div>
                      <div className="font-semibold text-slate-800">{item.title}</div>
                      <div className="text-sm text-slate-500">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { name: 'React', category: '前端框架', color: 'bg-blue-50 text-blue-600' },
                    { name: 'Node.js', category: '后端服务', color: 'bg-green-50 text-green-600' },
                    { name: 'PostgreSQL', category: '数据库', color: 'bg-indigo-50 text-indigo-600' },
                    { name: 'Redis', category: '缓存', color: 'bg-red-50 text-red-600' },
                    { name: 'Docker', category: '容器化', color: 'bg-sky-50 text-sky-600' },
                    { name: 'Kubernetes', category: '编排', color: 'bg-blue-50 text-blue-600' },
                    { name: 'RabbitMQ', category: '消息队列', color: 'bg-orange-50 text-orange-600' },
                    { name: 'Nginx', category: '网关', color: 'bg-emerald-50 text-emerald-600' },
                  ].map((tech) => (
                    <div key={tech.name} className="bg-slate-50 rounded-xl p-4 text-center hover:bg-slate-100 transition-colors">
                      <div className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-2 ${tech.color}`}>
                        {tech.category}
                      </div>
                      <div className="font-semibold text-slate-800">{tech.name}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-slate-900 mb-4">
            准备好开启智能化船运管理了吗？
          </h3>
          <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
            立即联系我们，获取专属解决方案，让船运发货管理更高效、更智能
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="group px-8 py-4 bg-gradient-to-r from-ocean-500 to-primary-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-ocean-500/30 transition-all duration-300 flex items-center gap-2"
            >
              免费咨询
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button className="px-8 py-4 bg-white text-slate-700 font-semibold rounded-xl border border-slate-200 hover:border-ocean-300 hover:bg-ocean-50 transition-all duration-300">
              查看案例
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
